import React, { useEffect, useMemo, useRef } from 'react'
import AutocompleteMiu from '@material-ui/lab/Autocomplete'
import CircularProgress from '@material-ui/core/CircularProgress'
import { FormGroup } from '@material-ui/core'
import { useIntl } from 'react-intl'
import clsx from 'clsx'
import _debounce from 'lodash/debounce'

import MaterialUI from '.'
import Http from '../../lib/http'
import './autocompleteAsync.css'

/** Default debounce delay (ms) before firing search requests while the user types. */
const DEFAULT_DEBOUNCE_MS = 750
const EMPTY_OPTIONS = []
const EMPTY_OBJECT = {}
const EMPTY_PARAMS = []

/**
 * Resolves which query keys receive the typed search string.
 *
 * @param {string[]} params - Default field names (e.g. `['search_text']`).
 * @param {(searchText: string) => string[]|undefined} [getDynamicParams] - Optional resolver per keystroke.
 * @param {string} searchText - Current debounced input value.
 * @returns {string[]}
 */
function resolveSearchParamFields(params, getDynamicParams, searchText) {
  if (typeof getDynamicParams === 'function') {
    const resolved = getDynamicParams(searchText)
    return Array.isArray(resolved) ? resolved : params
  }
  return params
}

/**
 * Builds the GET query object: `additionalParams` plus each search field set to `searchText`.
 *
 * @param {string} searchText
 * @param {string[]} params
 * @param {(searchText: string) => string[]|undefined} [getDynamicParams]
 * @param {Record<string, unknown>} additionalParams
 * @returns {Record<string, unknown>}
 */
function buildSearchQuery(searchText, params, getDynamicParams, additionalParams) {
  const fields = resolveSearchParamFields(params, getDynamicParams, searchText)
  const query = { ...additionalParams }

  if (Array.isArray(fields)) {
    fields.forEach((field) => {
      query[field] = searchText
    })
  }

  return query
}

/**
 * Normalizes common Laravel/list API shapes to a plain array.
 *
 * @param {unknown} response - Raw `resp.data` or pre-adapted payload.
 * @returns {unknown[]}
 */
function unwrapListResponse(response) {
  if (Array.isArray(response)) return response
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.data?.data)) return response.data.data
  return []
}

/**
 * Applies optional adapters and returns the option list for the dropdown.
 *
 * @param {unknown} response - API payload (before or after `responseAdaptar`).
 * @param {{ adapterOptions?: (raw: unknown) => unknown[], responseAdaptar?: (raw: unknown) => unknown, adapter?: (item: unknown) => unknown }} config
 * @returns {unknown[]}
 */
function normalizeOptions(response, { adapterOptions, responseAdaptar, adapter }) {
  let normalized = response

  if (adapterOptions instanceof Function) {
    return adapterOptions(normalized) || []
  }

  if (responseAdaptar instanceof Function) {
    normalized = responseAdaptar(normalized)
  }

  const list = unwrapListResponse(normalized)

  if (adapter instanceof Function) {
    return list.map(adapter)
  }

  return list
}

/**
 * Fallback label when `getOptionLabel` is not provided.
 *
 * @param {unknown} option
 * @returns {string}
 */
function defaultGetOptionLabel(option) {
  if (!option) return ''
  if (typeof option === 'string') return option
  return option.label || option.name || option.title || ''
}

/**
 * Stable key for deduplicating options across merged lists.
 *
 * @param {unknown} option
 * @param {(option: unknown) => string} [getOptionLabel]
 * @returns {string}
 */
function getOptionDedupeKey(option, getOptionLabel = defaultGetOptionLabel) {
  if (option == null) return ''
  if (typeof option !== 'object') return String(option)
  if (option.id != null && option.id !== '') return `id:${option.id}`
  if (option.value != null && option.value !== '') return `value:${option.value}`
  return `label:${getOptionLabel(option)}`
}

/**
 * Merges option lists in order; first occurrence of each dedupe key wins.
 *
 * @param {unknown[][]} lists
 * @param {(option: unknown) => string} [getOptionLabel]
 * @returns {unknown[]}
 */
function mergeUniqueOptions(lists, getOptionLabel = defaultGetOptionLabel) {
  const map = new Map()
  for (const list of lists) {
    if (!Array.isArray(list)) continue
    for (const option of list) {
      const key = getOptionDedupeKey(option, getOptionLabel)
      if (!map.has(key)) {
        map.set(key, option)
      }
    }
  }
  return Array.from(map.values())
}

/**
 * @typedef {Object} AutocompleteAsyncProps
 * @property {string[]} [params=[]] - Query param names that receive the typed search string (e.g. `['search_text']`).
 * @property {string} uri - Relative API path passed to `Http.get` (e.g. `'/api/vendors'`).
 * @property {string} [label] - Inline label on `MaterialUI.Input`. Prefer `FieldAddon` for label/required/error in new forms.
 * @property {unknown|null} state - Selected option (object or primitive); controlled value.
 * @property {string} [error] - Validation message; when `label` is set, passed to the input as error text.
 * @property {(value: unknown) => void} setState - Called on selection; receives raw option or `adapter(option)` when `returnAdaptedData`.
 * @property {React.ReactNode} [children] - End addon slot (e.g. create button); wraps field in `FormGroup` when present.
 * @property {(item: unknown) => unknown} [adapter] - Maps each list item after unwrap; also used on select when `returnAdaptedData`.
 * @property {(raw: unknown) => unknown} [responseAdaptar] - Legacy whole-response transform before list unwrap (typo preserved for callers).
 * @property {(raw: unknown) => unknown[]} [adapterOptions] - Replaces list normalization entirely when provided.
 * @property {unknown[]} [initOptions=[]] - Seed options when search is empty or after clear.
 * @property {import('@material-ui/core').InputLabelProps} [InputLabelProps] - Forwarded to `MaterialUI.Input`.
 * @property {(searchText: string) => string[]} [getDynamicParams] - Chooses search query keys per keystroke (falls back to `params`).
 * @property {boolean} [returnAdaptedData=false] - If true and `adapter` is set, `setState` receives `adapter(selectedOption)`.
 * @property {Record<string, unknown>} [additionalParams={}] - Static query params merged into every GET (search and `loadOnOpen`).
 * @property {boolean} [required] - Shows required indicator when `label` is set.
 * @property {string} [className] - Wrapper classes (`t4i-autocomplete-super--with-end` also enables end-addon layout).
 * @property {boolean} [withEndAddon=false] - Applies end-addon layout when there is no `children` slot.
 * @property {number} [debounceMs=750] - Debounce delay before search GET.
 * @property {(option: unknown) => string} [getOptionLabel] - Option display string; defaults to `label` / `name` / `title`.
 * @property {boolean} [loadOnOpen=false] - Fetches with `additionalParams` only when the dropdown opens (no search text).
 * @property {boolean} [disabled=false] - Disables input and prevents opening the list.
 * @property {(option: unknown, value: unknown) => boolean} [getOptionSelected] - MUI equality for controlled value; default compares `id` or labels.
 * @property {(props: object, option: unknown) => React.ReactNode} [renderOption] - Custom option row (e.g. `VendorOption`).
 * @property {boolean} [freeSolo] - Forwarded to MUI `Autocomplete` when needed.
 * @property {boolean} [disableClearable] - Forwarded to MUI `Autocomplete`.
 */

/**
 * Async search autocomplete backed by `Http.get` on `uri`.
 *
 * Typing debounces into a GET with search fields from `params` / `getDynamicParams` plus `additionalParams`.
 * Server-side filtering only (`filterOptions` passthrough). Selected value stays in the merged option list.
 *
 * In new screens, wrap with `FieldAddon` for label, `required`, and `error`; pass only `uri`, `state`, `setState`,
 * and `getOptionLabel` (plus `getOptionSelected` when values are objects with `id`).
 *
 * @param {AutocompleteAsyncProps & Omit<import('@material-ui/lab').AutocompleteProps, 'options'|'loading'|'renderInput'|'onChange'|'value'|'filterOptions'>} props
 * @returns {React.ReactElement}
 */
export default function AutocompleteAsync({
  params = EMPTY_PARAMS,
  uri,
  label,
  state,
  error,
  setState,
  children,
  adapter,
  responseAdaptar,
  adapterOptions,
  initOptions = EMPTY_OPTIONS,
  InputLabelProps = EMPTY_OBJECT,
  getDynamicParams,
  returnAdaptedData = false,
  additionalParams = EMPTY_OBJECT,
  required,
  className,
  withEndAddon = false,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  getOptionLabel: passedGetOptionLabel,
  loadOnOpen = false,
  disabled = false,
  ...others
}) {
  const { formatMessage: trans } = useIntl()

  const [loading, setLoading] = React.useState(false)
  const [key, setKey] = React.useState(0)
  const [searchDebounced, setSearchDebounced] = React.useState('')
  const [open, setOpen] = React.useState(false)
  const [options, setOptions] = React.useState(initOptions)

  const initOptionsRef = useRef(initOptions)
  const hadValueRef = useRef(Boolean(state))
  const stateRef = useRef(state)
  const fetchConfigRef = useRef({
    uri,
    params,
    getDynamicParams,
    additionalParams,
    adapter,
    responseAdaptar,
    adapterOptions,
  })

  const resolvedGetOptionLabel = passedGetOptionLabel || defaultGetOptionLabel
  const applyOptions = (...lists) => mergeUniqueOptions(lists, resolvedGetOptionLabel)

  useEffect(() => {
    stateRef.current = state
  }, [state])

  useEffect(() => {
    initOptionsRef.current = initOptions
    if (!searchDebounced) {
      setOptions(applyOptions(initOptions))
    }
  }, [initOptions, searchDebounced])

  useEffect(() => {
    fetchConfigRef.current = {
      uri,
      params,
      getDynamicParams,
      additionalParams,
      adapter,
      responseAdaptar,
      adapterOptions,
    }
  }, [uri, params, getDynamicParams, additionalParams, adapter, responseAdaptar, adapterOptions])

  useEffect(() => {
    const hadValue = hadValueRef.current
    hadValueRef.current = Boolean(state)
    if (!state && hadValue) {
      setKey((prev) => prev + 1)
      setOptions(EMPTY_OPTIONS)
      setSearchDebounced('')
    }
  }, [state])

  const debounceFn = useMemo(
    () =>
      _debounce((value) => {
        setSearchDebounced(value)
      }, debounceMs),
    [debounceMs],
  )

  useEffect(() => () => debounceFn.cancel(), [debounceFn])

  useEffect(() => {
    let active = true

    if (!searchDebounced) {
      setOptions(applyOptions(initOptionsRef.current || []))
      setLoading(false)
      return () => {
        active = false
      }
    }

    ;(async () => {
      try {
        setLoading(true)
        const config = fetchConfigRef.current
        const query = buildSearchQuery(searchDebounced, config.params, config.getDynamicParams, config.additionalParams)
        const resp = await Http.get(config.uri, { params: query })

        if (!active) return

        if (resp?.status >= 200 && resp?.status < 300) {
          const nextOptions = normalizeOptions(resp.data, config)
          const currentState = stateRef.current
          setOptions(applyOptions(nextOptions, currentState ? [currentState] : []))
        } else {
          setOptions([])
        }
      } catch (_err) {
        if (active) {
          setOptions([])
        }
      } finally {
        if (active) {
          setLoading(false)
        }
      }
    })()

    return () => {
      active = false
    }
  }, [searchDebounced])

  useEffect(() => {
    if (!open || !loadOnOpen || searchDebounced) return

    let active = true
    setLoading(true)
    ;(async () => {
      try {
        const config = fetchConfigRef.current
        const resp = await Http.get(config.uri, { params: { ...config.additionalParams } })
        if (!active) return
        if (resp?.status >= 200 && resp?.status < 300) {
          const fetched = normalizeOptions(resp.data, config)
          const currentState = stateRef.current
          setOptions(applyOptions(fetched, currentState ? [currentState] : []))
        } else {
          setOptions(applyOptions(initOptionsRef.current || []))
        }
      } catch (_err) {
        if (active) setOptions(applyOptions(initOptionsRef.current || []))
      } finally {
        if (active) setLoading(false)
      }
    })()

    return () => {
      active = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const handleChange = (_, newValue) => {
    if (newValue && returnAdaptedData && adapter instanceof Function) {
      setState(adapter(newValue))
    } else {
      setState(newValue)
    }
    setSearchDebounced('')
    debounceFn.cancel()
    setLoading(false)
  }

  const handleSearchChange = (value) => {
    if (value) {
      setLoading(true)
      debounceFn(value)
      return
    }

    debounceFn.cancel()
    setSearchDebounced('')
    setOptions(applyOptions(initOptionsRef.current || []))
    setLoading(false)
  }

  const handleOpen = () => {
    if (disabled) return
    setOpen(true)
    if (!loadOnOpen && !searchDebounced) {
      setOptions(applyOptions(initOptionsRef.current || []))
    }
  }

  const handleClose = () => {
    setOpen(false)
  }

  const noOptionsText = trans({ id: 'no_results_found', defaultMessage: 'No results found' })
  const loadingText = trans({ id: 'loading', defaultMessage: 'Loading...' })

  const { getOptionLabel: _omitGetOptionLabel, getOptionSelected: passedGetOptionSelected, placeholder, ...restOthers } = others

  const defaultGetOptionSelected = (option, value) => {
    if (!option || !value) return option === value
    if (option.id != null && value.id != null) return option.id === value.id
    return resolvedGetOptionLabel(option) === resolvedGetOptionLabel(value)
  }

  const hasValidationError = Boolean(error)

  const renderInput = (inputParams) => {
    const { InputProps: paramsInputProps, ...restInputParams } = inputParams

    return (
      <MaterialUI.Input
        {...restInputParams}
        placeholder={placeholder}
        margin="none"
        size="small"
        label={label}
        required={required}
        error={label && error ? error : undefined}
        disabled={disabled}
        inputProps={{ ...(restInputParams.inputProps || {}), 'aria-invalid': hasValidationError || undefined }}
        InputLabelProps={InputLabelProps}
        InputProps={{
          ...paramsInputProps,
          readOnly: disabled || paramsInputProps.readOnly,
          endAdornment: (
            <>
              {loading && open ? <CircularProgress color="inherit" size={20} /> : null}
              {paramsInputProps.endAdornment}
            </>
          ),
        }}
      />
    )
  }

  const autocompleteProps = {
    key,
    fullWidth: true,
    size: 'small',
    disabled,
    open: disabled ? false : open,
    onOpen: handleOpen,
    onClose: handleClose,
    options,
    loading: loading && open,
    onChange: handleChange,
    onInputChange: (_event, newInputValue, reason) => {
      if (reason === 'input') {
        handleSearchChange(newInputValue)
      } else if (reason === 'clear') {
        handleSearchChange('')
      }
    },
    filterOptions: (items) => items,
    value: state ?? null,
    noOptionsText,
    loadingText,
    renderInput,
    ...restOthers,
    getOptionLabel: resolvedGetOptionLabel,
    getOptionSelected: passedGetOptionSelected || defaultGetOptionSelected,
  }

  const wrapperClass = clsx(
    't4i-autocomplete-super',
    (withEndAddon || !!children || /\bt4i-autocomplete-super--with-end\b/.test(className || '')) && 't4i-autocomplete-super--with-end',
    disabled && 't4i-autocomplete-super--disabled',
    hasValidationError && 't4i-autocomplete-super--invalid',
    className,
  )

  return (
    <div className="w-100">
      {children ? (
        <FormGroup className="flex-row flex-nowrap input-addon-group w-100">
          <div className={clsx(wrapperClass, 't4i-autocomplete-super__field')}>
            <AutocompleteMiu {...autocompleteProps} className="t4i-autocomplete-super__control" />
          </div>
          {children}
        </FormGroup>
      ) : (
        <div className={wrapperClass}>
          <AutocompleteMiu {...autocompleteProps} className="t4i-autocomplete-super__control" />
        </div>
      )}
    </div>
  )
}
